import React, { useState } from 'react'
import { cn } from '@/lib/utils'

/** The small pieces every deal section is built from, in the light palette */

export const money = (n?: number | null) =>
  n && n > 0 ? `$${Math.round(n).toLocaleString('en-US')}` : 'Product + bonuses'

/** "Oct 15" from a YYYY-MM-DD or ISO value */
export const day = (v?: string | null, year = false) =>
  v
    ? new Date(v.length === 10 ? `${v}T12:00:00` : v).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(year ? { year: 'numeric' } : {}),
      })
    : ''

export const fieldCls =
  'w-full rounded-xl border border-border bg-[#FFFFFF] px-3.5 py-3 text-base text-foreground placeholder:text-muted-foreground outline-none transition focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40 disabled:opacity-60'
export const primaryCls =
  'inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] px-4 text-base font-semibold text-[#0F172A] transition-colors hover:bg-[#B2F25E] disabled:opacity-50'
export const secondaryCls =
  'inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 text-base font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50'
export const linkCls = 'text-sm font-semibold text-[#3F7A0A] underline underline-offset-2'

export const Card: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { tone?: 'plain' | 'lime' | 'amber' | 'blue' }
> = ({ className, tone = 'plain', ...props }) => (
  <div
    className={cn(
      'rounded-2xl border p-4',
      tone === 'lime' && 'border-[#C9E9A6] bg-[#F7FEE7]',
      tone === 'amber' && 'border-[#FCD9A8] bg-[#FFF4E5]',
      tone === 'blue' && 'border-[#BFD7FB] bg-[#EFF6FF]',
      tone === 'plain' && 'border-border bg-card shadow-sm',
      className,
    )}
    {...props}
  />
)

export const Eyebrow: React.FC<{
  children: React.ReactNode
  tone?: 'lime' | 'muted' | 'amber'
}> = ({ children, tone = 'muted' }) => (
  <p
    className={cn(
      'text-xs font-bold uppercase tracking-wider',
      tone === 'lime' && 'text-[#4D7C0F]',
      tone === 'amber' && 'text-[#B45309]',
      tone === 'muted' && 'text-muted-foreground',
    )}
  >
    {children}
  </p>
)

export type Tone = 'green' | 'blue' | 'amber' | 'red' | 'grey'
const TONE: Record<Tone, string> = {
  green: 'bg-[#E8F9D2] text-[#2F5E06] border-[#C9E9A6]',
  blue: 'bg-[#E0EDFF] text-[#1D4ED8] border-[#BFD7FB]',
  amber: 'bg-[#FFF4E5] text-[#B45309] border-[#FCD9A8]',
  red: 'bg-[#FEECEC] text-[#B91C1C] border-[#FBC4C4]',
  grey: 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]',
}
export const Pill: React.FC<{ tone: Tone; children: React.ReactNode; className?: string }> = ({
  tone,
  children,
  className,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold',
      TONE[tone],
      className,
    )}
  >
    {children}
  </span>
)

/** One or more links (live posts, drafts), with an optional note */
export const LinksForm: React.FC<{
  label: string
  placeholder: string
  button: string
  note?: boolean
  busy: boolean
  onSubmit: (links: string[], note: string) => void
}> = ({ label, placeholder, button, note, busy, onSubmit }) => {
  const [links, setLinks] = useState([''])
  const [text, setText] = useState('')
  const valid = links.some((l) => /^https?:\/\//i.test(l.trim()))
  return (
    <div className="mt-3 space-y-2">
      <p className="text-sm text-foreground">{label}</p>
      {links.map((l, i) => (
        <input
          key={i}
          value={l}
          onChange={(e) => setLinks(links.map((x, j) => (j === i ? e.target.value : x)))}
          placeholder={placeholder}
          inputMode="url"
          className={fieldCls}
        />
      ))}
      <button type="button" onClick={() => setLinks([...links, ''])} className={linkCls}>
        + Add another link
      </button>
      {note ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Anything the brand should know (optional)"
          className={`${fieldCls} min-h-20 resize-none`}
        />
      ) : null}
      <button
        type="button"
        className={primaryCls}
        disabled={!valid || busy}
        onClick={() =>
          onSubmit(
            links.map((l) => l.trim()).filter((l) => /^https?:\/\//i.test(l)),
            text.trim(),
          )
        }
      >
        {busy ? 'Sending…' : button}
      </button>
    </div>
  )
}

/** Appearance check-in: a note now; the photo upload comes with the camera work later */
export const CheckIn: React.FC<{ busy: boolean; onSubmit: (note: string) => void }> = ({
  busy,
  onSubmit,
}) => {
  const [note, setNote] = useState('')
  return (
    <div className="mt-3 space-y-2">
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Where are you and who did you meet? (optional)"
        className={`${fieldCls} min-h-20 resize-none`}
      />
      <button
        type="button"
        className={primaryCls}
        disabled={busy}
        onClick={() => onSubmit(note.trim())}
      >
        Check in
      </button>
    </div>
  )
}

const slugOf = (m: any) => m.slug ?? m.type_slug ?? ''

/** Carrier, tracking and arrival, as the brand entered them when it shipped */
export const ShippingDetails: React.FC<{ milestones: any[] }> = ({ milestones }) => {
  const meta = milestones.find((m) => slugOf(m) === 'product_shipped')?.metadata ?? {}
  if (!meta.tracking) return null
  return (
    <div className="mt-3 rounded-xl bg-muted p-3 text-sm">
      <p className="font-semibold text-foreground">
        {[meta.carrier, meta.tracking].filter(Boolean).join(' ')}
      </p>
      {meta.arrives ? <p className="text-muted-foreground">Arrives {day(meta.arrives)}</p> : null}
    </div>
  )
}

/** Where and when to show up, as the brand entered it on its "Prepare" step */
export const AppearanceDetails: React.FC<{ milestones: any[] }> = ({ milestones }) => {
  const meta = milestones.find((m) => slugOf(m) === 'prepare_appearance')?.metadata ?? {}
  if (!meta.location && !meta.starts) return null
  const starts = meta.starts
    ? new Date(meta.starts).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null
  return (
    <div className="mt-3 rounded-xl bg-muted p-3 text-sm">
      {meta.location ? <p className="font-semibold text-foreground">{meta.location}</p> : null}
      <p className="text-foreground">{[starts, meta.hours].filter(Boolean).join(' · ')}</p>
      {meta.contact ? <p className="text-muted-foreground">Ask for {meta.contact}</p> : null}
    </div>
  )
}
